import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui";
import { Input } from "./components/ui";
import { Textarea } from "./components/ui";
import { Label } from "./components/ui";
import { UploadCloud, Plus, Trash2 } from "lucide-react";
import { Button } from "./components/ui";

// ----------------- Helpers -----------------
const normStr = (v: any) => (typeof v === "string" ? v.trim() : v);

function splitDropdown(raw?: string): string[] {
  if (!raw) return [];
  return raw
    .split(/\r?\n|,/)
    .map((s) => s.trim().replace(/^[-\s]+/, "")) // strip leading hyphens/spaces
    .filter(Boolean);
}

function normalizeDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`; // MM/DD/YYYY
}

function htmlInputType(t?: string): string {
  const s = (t || "").toLowerCase();
  if (s.includes("date")) return "date";
  if (s.includes("email")) return "email";
  if (s.includes("phone")) return "tel";
  if (s.includes("numeric") || s.includes("number")) return "number";
  return "text";
}

// ----------------- Dev Tests -----------------
// Rendered when no sheet is uploaded to ensure behaviors are covered.
const DEV_ROWS: any[] = [
  // Module A - field list
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Basics", "UI Field Grouping Type": "Field List", "UI Field Label": "Customer ID#", "UI Field Type": "Numeric", "Is Always Req?": "Y", "Default Value": "1001" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Basics", "UI Field Grouping Type": "Field List", "UI Field Label": "Contact Email", "UI Field Type": "Email", "Default Value": "user@example.com" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Location", "UI Field Grouping Type": "Field List", "UI Field Label": "Latitude Degrees", "UI Field Type": "Numeric", "Default Value": "34" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Location", "UI Field Grouping Type": "Field List", "UI Field Label": "Latitude Direction (N)", "UI Field Type": "Single-Select Dropdown", "Dropdown List of Values": "North (N),South (S),East (E),West (W)", "Default Value": "N" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Location", "UI Field Grouping Type": "Field List", "UI Field Label": "Longitude Degrees", "UI Field Type": "Numeric", "Default Value": "112" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Location", "UI Field Grouping Type": "Field List", "UI Field Label": "Longitude Direction (W)", "UI Field Type": "Single-Select Dropdown", "Dropdown List of Values": "West (W),East (E)", "Default Value": "W" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Meta", "UI Field Grouping Type": "Field List", "UI Field Label": "Updated By", "UI Field Type": "Text", "Default Value": "admin" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Meta", "UI Field Grouping Type": "Field List", "UI Field Label": "Updated Date", "UI Field Type": "Date", "Default Value": "2025-10-28" },
  { Module: "Module A", "UI Tab": "General", "UI Field Grouping": "Meta", "UI Field Grouping Type": "Field List", "UI Field Label": "Location Comments", "UI Field Type": "Text" },

  // Module B - grid example
  { Module: "Module B", "UI Tab": "Assets", "UI Field Grouping": "Rows", "UI Field Grouping Type": "Grid", "UI Field Label": "Asset Name", "UI Field Type": "Text", "Default Value": "Truck" },
  { Module: "Module B", "UI Tab": "Assets", "UI Field Grouping": "Rows", "UI Field Grouping Type": "Grid", "UI Field Label": "In Service?", "UI Field Type": "Single-Select Dropdown", "Dropdown List of Values": "-Yes,-No", "Default Value": "Yes" },
];

// ----------------- Component -----------------
export default function SpreadsheetFormMockup() {
  const [fields, setFields] = useState<any[]>([]);
  const [fileKey, setFileKey] = useState(0);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
        setFields(rows);
      } catch (err) {
        console.error("Error parsing sheet:", err);
      }
    };
    reader.readAsArrayBuffer(file);

    // allow re-upload of the same file
    e.target.value = "";
    setFileKey((k) => k + 1);
  }

  // If no sheet uploaded, show dev tests as source
  const sourceRows = fields.length > 0 ? fields : DEV_ROWS;

  // Build structure: Module → Tab → Group
  const modules = useMemo(() => {
    const acc: Record<string, Record<string, Record<string, { groupType: string; fields: any[] }>>> = {};
    sourceRows.forEach((row) => {
      const moduleName = normStr(row["Module"]) || "General";
      const tab = normStr(row["UI Tab"]) || "General";
      const group = normStr(row["UI Field Grouping"]) || "Fields";
      const groupType = normStr(row["UI Field Grouping Type"]) || "Field List";
      if (!acc[moduleName]) acc[moduleName] = {};
      if (!acc[moduleName][tab]) acc[moduleName][tab] = {};
      if (!acc[moduleName][tab][group]) acc[moduleName][tab][group] = { groupType, fields: [] };
      acc[moduleName][tab][group].fields.push(row);
    });
    return acc;
  }, [sourceRows]);

  // ----------------- Grid Component (FIX for FieldGrid not defined) -----------------
  function FieldGrid({ columns }: { columns: any[] }) {
    const labelFor = (col: any, i: number) => col["UI Field Label"] || col["Label"] || `Field ${i + 1}`;
    const keyFor = (col: any, i: number) => labelFor(col, i);
    const buildDefaultRow = () => Object.fromEntries(columns.map((c, i) => [keyFor(c, i), c["Default Value"]?.trim?.() || ""]));
    const buildBlankRow = () => Object.fromEntries(columns.map((c, i) => [keyFor(c, i), ""]));

    const [rows, setRows] = useState<Record<string, string>[]>([buildDefaultRow()]);

    const setCell = (rIdx: number, key: string, val: string) => {
      setRows((prev) => {
        const next = [...prev];
        next[rIdx] = { ...next[rIdx], [key]: val };
        return next;
      });
    };

    const addRow = () => setRows((prev) => [...prev, buildBlankRow()]);
    const removeRow = (idx: number) => setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

    return (
      <div className="space-y-3">
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" className="rounded-2xl" onClick={addRow}>
            <Plus className="w-4 h-4 mr-1" /> Add Row
          </Button>
        </div>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {columns.map((c: any, i: number) => (
                <th key={i} className="text-left border-b p-2">{labelFor(c, i)}</th>
              ))}
              <th className="text-right border-b p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rIdx) => (
              <tr key={rIdx}>
                {columns.map((c: any, cIdx: number) => {
                  const key = keyFor(c, cIdx);
                  const readonly = `${c["Is Read-Only?"]}`.toLowerCase().startsWith("y");
                  const dropdown = splitDropdown(c["Dropdown List of Values"]);
                  const value = row[key] ?? "";
                  return (
                    <td key={key} className={`p-2 border-b ${readonly ? "opacity-60" : ""}`}>
                      {dropdown.length > 0 ? (
                        <select
                          disabled={readonly}
                          value={value}
                          onChange={(e) => !readonly && setCell(rIdx, key, e.target.value)}
                          className="border rounded-md p-2 w-full"
                        >
                          <option value="">Select...</option>
                          {dropdown.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type={htmlInputType(c["UI Field Type"])}
                          disabled={readonly}
                          value={value}
                          onChange={(e) => !readonly && setCell(rIdx, key, e.target.value)}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="p-2 border-b text-right">
                  <Button type="button" variant="ghost" onClick={() => removeRow(rIdx)} disabled={rows.length <= 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ----------------- Layout Rules -----------------
  const specialRow = ["updated by", "updated date"];
  const isCommentsField = (label: string) => label.toLowerCase().includes("comments");
  const isLatitudeField = (label: string) => label.toLowerCase().includes("latitude");
  const isLongitudeField = (label: string) => label.toLowerCase().includes("longitude");

  function renderField(f: any, i: number) {
    const label = f["UI Field Label"] || f["Label"] || `Field ${i + 1}`;
    const defRaw = f["Default Value"]?.trim?.() || "";
    const defVal = htmlInputType(f["UI Field Type"]) === "date" ? normalizeDate(defRaw) : defRaw;
    const readonly = `${f["Is Read-Only?"]}`.toLowerCase().startsWith("y");
    const required = `${f["Is Always Req?"]}`.toLowerCase().startsWith("y");
    const dropdown = splitDropdown(f["Dropdown List of Values"]);
    const type = (f["UI Field Type"] || "Text").toLowerCase();

    if (dropdown.length > 0) {
      return (
        <div key={i} className={`space-y-2 ${readonly ? "opacity-60" : ""}`}>
          <Label>
            {label}
            {required && <span className="text-red-600"> *</span>}
          </Label>
          <select disabled={readonly} defaultValue={defVal || undefined} className="border rounded-md p-2 w-full">
            <option value="">Select...</option>
            {dropdown.map((opt: string) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (type.includes("textarea")) {
      return (
        <div key={i} className={`space-y-2 ${readonly ? "opacity-60" : ""}`}>
          <Label>
            {label}
            {required && <span className="text-red-600"> *</span>}
          </Label>
          <Textarea defaultValue={defVal} disabled={readonly} />
        </div>
      );
    }

    return (
      <div key={i} className={`space-y-2 ${readonly ? "opacity-60" : ""}`}>
        <Label>
          {label}
          {required && <span className="text-red-600"> *</span>}
        </Label>
        <Input type={htmlInputType(f["UI Field Type"])} defaultValue={defVal} disabled={readonly} />
      </div>
    );
  }

  // ----------------- Render -----------------
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-semibold">Spreadsheet UI Mockup</h1>
          <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-2xl cursor-pointer hover:shadow-sm">
            <UploadCloud className="w-4 h-4" /> Upload spec (.xlsx)
            <input key={fileKey} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
          </label>
        </div>

        <Tabs defaultValue={Object.keys(modules)[0] || "General"}>
          <TabsList className="flex flex-wrap">
            {Object.keys(modules).map((moduleName) => (
              <TabsTrigger key={moduleName} value={moduleName}>
                {moduleName}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(modules).map(([moduleName, tabs]) => (
            <TabsContent key={moduleName} value={moduleName}>
              <Tabs defaultValue={Object.keys(tabs)[0] || "General"}>
                <TabsList className="flex flex-wrap">
                  {Object.keys(tabs).map((tab) => (
                    <TabsTrigger key={tab} value={tab}>
                      {tab}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {Object.entries(tabs).map(([tab, groups]) => (
                  <TabsContent key={tab} value={tab}>
                    <div className="grid grid-cols-1 gap-4 mt-4">
                      {Object.entries(groups).map(([groupName, info]) => (
                        <Card key={groupName} className="rounded-2xl shadow-sm">
                          <CardHeader>
                            <CardTitle className="text-base">{groupName}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {String(info.groupType).toLowerCase() === "grid" ? (
                              <FieldGrid columns={info.fields} />
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                {(() => {
                                  // partition fields into buckets for layout rules
                                  const getLabel = (f: any) => f["UI Field Label"] || f["Label"] || "";

                                  const regularFields = info.fields.filter((f: any) => {
                                    const l = getLabel(f);
                                    return (
                                      !specialRow.includes(l.toLowerCase()) &&
                                      !isCommentsField(l) &&
                                      !isLatitudeField(l) &&
                                      !isLongitudeField(l)
                                    );
                                  });

                                  const commentsAndUpdated = info.fields.filter((f: any) => {
                                    const l = getLabel(f);
                                    return specialRow.includes(l.toLowerCase()) || isCommentsField(l);
                                  });

                                  const latitudeFields = info.fields.filter((f: any) => isLatitudeField(getLabel(f)));
                                  const longitudeFields = info.fields.filter((f: any) => isLongitudeField(getLabel(f)));

                                  return (
                                    <>
                                      {regularFields.map(renderField)}

                                      {latitudeFields.length > 0 && (
                                        <div className="col-span-full grid grid-cols-5 gap-4">
                                          {latitudeFields.map(renderField)}
                                        </div>
                                      )}

                                      {longitudeFields.length > 0 && (
                                        <div className="col-span-full grid grid-cols-5 gap-4">
                                          {longitudeFields.map(renderField)}
                                        </div>
                                      )}

                                      {commentsAndUpdated.length > 0 && (
                                        <div className="col-span-full grid grid-cols-3 gap-4">
                                          {commentsAndUpdated.map(renderField)}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
